<template>
  <Teleport to="body">
    <Transition name="bc-fade">
      <div v-if="broadcastComposeOpen" class="bc-overlay" @mousedown.self="close">
        <div class="bc-box" role="dialog" aria-label="Enviar aviso para agentes">
          <div class="bc-icon">📢</div>
          <div class="bc-title">Aviso para agentes</div>
          <div class="bc-desc">Escreva uma mensagem e escolha quem vai receber. Cada agente confirma o recebimento na própria tela.</div>

          <div class="bc-field">
            <div class="bc-ta-wrap">
              <textarea
                ref="taEl"
                v-model="body"
                class="bc-textarea"
                rows="4"
                placeholder="Escreva o aviso…"
                maxlength="2000"
              ></textarea>
              <EmojiPicker class="bc-emoji" @pick="insertEmoji" />
            </div>
          </div>

          <div class="bc-field-row">
            <input v-model.trim="imageUrl" type="text" class="bc-input" placeholder="URL de imagem (opcional)" />
            <input v-model.trim="youtubeUrl" type="text" class="bc-input" placeholder="Link do YouTube (opcional)" />
          </div>
          <div class="bc-field">
            <input v-model.trim="linkUrl" type="text" class="bc-input" placeholder="Link (opcional)" />
          </div>

          <div v-if="imageUrl || youtubeEmbedUrl" class="bc-preview">
            <img v-if="imageUrl" :src="imageUrl" alt="Preview" class="bc-preview-img" @error="onImageError" />
            <iframe
              v-if="youtubeEmbedUrl"
              :src="youtubeEmbedUrl"
              class="bc-preview-video"
              frameborder="0"
              allowfullscreen
            ></iframe>
          </div>

          <div class="bc-field">
            <div class="bc-agents-head">
              <span class="bc-agents-label">Destinatários ({{ selectedAgentIds.length }})</span>
              <button type="button" class="bc-link-btn" @click="toggleSelectAll">
                {{ allSelected ? 'Limpar seleção' : 'Selecionar todos' }}
              </button>
            </div>
            <input v-model.trim="agentSearch" type="text" class="bc-input bc-agents-search" placeholder="Buscar agente…" />
            <div class="bc-agents-list">
              <div v-if="loadingAgents" class="bc-agents-empty">Carregando agentes…</div>
              <div v-else-if="!filteredAgents.length" class="bc-agents-empty">Nenhum agente encontrado</div>
              <label v-for="a in filteredAgents" :key="a.id" class="bc-agent-row">
                <input type="checkbox" :checked="selectedAgentIds.includes(a.id)" @change="toggleAgent(a.id)" />
                <span class="bc-agent-av" :style="{ background: avatarColor(a.name) }">
                  <img v-if="a.avatarUrl" :src="a.avatarUrl" :alt="a.name" @error="(e) => (e.target as HTMLImageElement).style.display='none'" />
                  <span v-else>{{ initials(a.name) }}</span>
                </span>
                <span class="bc-agent-name">{{ a.name }}</span>
              </label>
            </div>
          </div>

          <div v-if="sendError" class="bc-error">{{ sendError }}</div>

          <div class="bc-actions">
            <button type="button" class="bc-btn bc-btn--muted" :disabled="sending" @click="close">Cancelar</button>
            <button type="button" class="bc-btn bc-btn--primary" :disabled="!canSubmit" @click="submit">
              {{ sending ? 'Enviando…' : 'Enviar aviso' }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch } from 'vue'
import { useStore } from 'vuex'
import Swal from 'sweetalert2'
import EmojiPicker from '@/components/dashboard/EmojiPicker.vue'
import { avatarColor, initials } from '@/composables/useHelpers'
import { useAgentChat } from '@/composables/useAgentChat'
import { useBroadcast } from '@/composables/useBroadcast'
import { broadcastComposeOpen, closeBroadcastCompose } from '@/composables/useBroadcastCompose'
import { parseYoutubeId } from '@/utils/youtube'
import type { AuthUser } from '@/types/auth'

const store = useStore()
const { agents, loadingAgents, loadAgents } = useAgentChat()
const { sending, sendError, sendBroadcast } = useBroadcast()

const taEl = ref<HTMLTextAreaElement | null>(null)
const body = ref('')
const imageUrl = ref('')
const youtubeUrl = ref('')
const linkUrl = ref('')
const agentSearch = ref('')
const selectedAgentIds = ref<number[]>([])
const imageLoadFailed = ref(false)

const filteredAgents = computed(() => {
  const q = agentSearch.value.toLowerCase()
  return agents.value.filter(a => !q || a.name.toLowerCase().includes(q))
})

const allSelected = computed(() => agents.value.length > 0 && selectedAgentIds.value.length === agents.value.length)

const canSubmit = computed(() =>
  body.value.trim().length > 0 && selectedAgentIds.value.length > 0 && !sending.value
)

const youtubeEmbedUrl = computed(() => {
  if (!youtubeUrl.value) return null
  const id = parseYoutubeId(youtubeUrl.value)
  return id ? `https://www.youtube.com/embed/${id}` : null
})

function onImageError(e: Event) {
  imageLoadFailed.value = true
  ;(e.target as HTMLImageElement).style.display = 'none'
}

// Mesmo padrão de inserção com cursor de FloatChatWindow.vue (insertEmoji).
function insertEmoji(em: string) {
  const el = taEl.value
  if (!el) { body.value += em; return }
  const start = el.selectionStart ?? body.value.length
  const end   = el.selectionEnd   ?? body.value.length
  body.value = body.value.slice(0, start) + em + body.value.slice(end)
  nextTick(() => {
    el.selectionStart = el.selectionEnd = start + em.length
    el.focus()
  })
}

function toggleAgent(id: number) {
  const idx = selectedAgentIds.value.indexOf(id)
  if (idx === -1) selectedAgentIds.value.push(id)
  else selectedAgentIds.value.splice(idx, 1)
}

function toggleSelectAll() {
  selectedAgentIds.value = allSelected.value ? [] : agents.value.map(a => a.id)
}

function resetForm() {
  body.value = ''
  imageUrl.value = ''
  youtubeUrl.value = ''
  linkUrl.value = ''
  agentSearch.value = ''
  selectedAgentIds.value = []
  imageLoadFailed.value = false
  sendError.value = ''
}

function close() {
  if (sending.value) return
  closeBroadcastCompose()
  resetForm()
}

async function submit() {
  if (!canSubmit.value) return
  const user = store.getters.authUser as AuthUser | null
  if (!user) return

  try {
    await sendBroadcast({
      senderId:     user.id,
      body:         body.value.trim(),
      imageUrl:     imageUrl.value || undefined,
      youtubeUrl:   youtubeUrl.value || undefined,
      linkUrl:      linkUrl.value || undefined,
      recipientIds: selectedAgentIds.value,
    })
    closeBroadcastCompose()
    resetForm()
    Swal.fire({ icon: 'success', title: 'Aviso enviado!', timer: 2000, showConfirmButton: false })
  } catch {
    // sendError já foi setado por useBroadcast — segue exibido no modal, que permanece aberto.
  }
}

// Carrega a lista de agentes só se ainda não tiver sido carregada por outro
// consumidor (AgentChatDrawer.vue já chama loadAgents no mount da app) —
// evita repetir a chamada sempre que o compositor abrir.
watch(broadcastComposeOpen, (open) => {
  if (!open) return
  const user = store.getters.authUser as AuthUser | null
  if (user && !agents.value.length && !loadingAgents.value) {
    loadAgents(user.access_token, user.id)
  }
})
</script>

<style scoped>
.bc-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: rgba(0, 0, 0, .55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.bc-box {
  background: var(--white);
  border-radius: var(--r);
  padding: 32px;
  width: 480px;
  max-width: 100%;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, .3);
  text-align: center;
}

.bc-icon { font-size: 32px; margin-bottom: 4px; }

.bc-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--ink-80);
  margin-bottom: 6px;
}

.bc-desc {
  font-size: 13px;
  color: var(--ink-40);
  line-height: 1.5;
  margin-bottom: 20px;
}

.bc-field { margin-bottom: 12px; text-align: left; }
.bc-field-row { display: flex; gap: 8px; margin-bottom: 12px; }
.bc-field-row .bc-input { flex: 1; min-width: 0; }

.bc-ta-wrap { position: relative; }
.bc-textarea {
  width: 100%;
  border: 1px solid var(--ink-10);
  border-radius: var(--r-sm);
  padding: 10px 36px 10px 12px;
  font-family: inherit;
  font-size: 14px;
  resize: vertical;
  min-height: 90px;
}
.bc-emoji { position: absolute; top: 4px; right: 4px; }

.bc-input {
  width: 100%;
  border: 1px solid var(--ink-10);
  border-radius: var(--r-sm);
  padding: 9px 12px;
  font-family: inherit;
  font-size: 13.5px;
}

.bc-preview {
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.bc-preview-img {
  max-width: 100%;
  max-height: 160px;
  border-radius: var(--r-sm);
  object-fit: contain;
  align-self: center;
}
.bc-preview-video {
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: var(--r-sm);
}

.bc-agents-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}
.bc-agents-label { font-size: 12.5px; font-weight: 700; color: var(--ink-80); }
.bc-link-btn {
  border: none;
  background: transparent;
  color: var(--red, #BC0C24);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}
.bc-agents-search { margin-bottom: 8px; }

.bc-agents-list {
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid var(--ink-10);
  border-radius: var(--r-sm);
}
.bc-agents-empty {
  padding: 16px;
  text-align: center;
  font-size: 13px;
  color: var(--ink-40);
}
.bc-agent-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  cursor: pointer;
  border-bottom: 1px solid var(--ink-05);
}
.bc-agent-row:last-child { border-bottom: none; }
.bc-agent-row:hover { background: var(--ink-03, rgba(0,0,0,.02)); }
.bc-agent-av {
  width: 26px; height: 26px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 11px; font-weight: 700; flex-shrink: 0; overflow: hidden;
}
.bc-agent-av img { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; display: block; }
.bc-agent-name { font-size: 13px; color: var(--ink-80); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.bc-error {
  color: #b91c1c;
  font-size: 12.5px;
  margin-bottom: 12px;
  text-align: left;
}

.bc-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.bc-btn {
  padding: 10px 20px;
  border-radius: var(--r-sm);
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  border: none;
  font-family: inherit;
}
.bc-btn:disabled { opacity: .6; cursor: not-allowed; }
.bc-btn--primary { background: #BC0C24; color: #fff; }
.bc-btn--muted { background: transparent; color: var(--ink-40); border: 1px solid var(--ink-10); }

.bc-fade-enter-active,
.bc-fade-leave-active { transition: opacity .15s ease; }
.bc-fade-enter-from,
.bc-fade-leave-to { opacity: 0; }
</style>
